import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import dayjs from 'dayjs';
import { Typography, Tabs, Table, Spin, Segmented, Select, Checkbox, Tooltip as AntTooltip, Space } from 'antd';
import {
  ComposedChart, Bar, Cell, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LabelList, ResponsiveContainer,
} from 'recharts';
import { Link } from 'react-router-dom';
import FilterPanel from '../components/FilterPanel';
import StaffingTab from '../components/StaffingTab';
import ResultsTable from '../components/ResultsTable';
import SidePanel from '../components/SidePanel';
import api from '../api';

const REFRESH_MINUTES = 10;

// ── Formatters ───────────────────────────────────────────────────────────────

function fmtHMS(seconds) {
  if (seconds == null) return '-';
  const tot = Math.round(seconds);
  const h = Math.floor(tot / 3600);
  const m = Math.floor((tot % 3600) / 60);
  const s = tot % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function fmtHour(h) {
  if (h === 0)  return '12AM';
  if (h < 12)  return `${h}AM`;
  if (h === 12) return '12PM';
  return `${h - 12}PM`;
}

const HMS = (v) => fmtHMS(v);
const NUM = (v) => (v == null ? '-' : v.toLocaleString());

function renderQtyPct(v, row) {
  const qty = v == null ? '-' : v.toLocaleString();
  const pct = (row.HandledQty > 0 && v != null)
    ? `${Math.round((v / row.HandledQty) * 100)}%`
    : '-';
  return (
    <div>
      <div>{qty}</div>
      <div style={{ fontSize: 11, color: '#888' }}>{pct}</div>
    </div>
  );
}

// ── Chart colors ─────────────────────────────────────────────────────────────

const CHART_COLORS = [
  '#4096ff', '#fa8c16', '#13c2c2', '#722ed1', '#eb2f96',
  '#52c41a', '#fadb14', '#f5222d', '#2f54eb', '#d4380d',
];
const getColor = (i) => CHART_COLORS[i % CHART_COLORS.length];

const LOOKBACK_OPTIONS = [1, 2, 3, 4, 6, 8, 12, 16, 20, 24, 28, 32, 36, 40, 44, 48, 52].map(
  (n) => ({ value: n, label: String(n) })
);

// ── Metric box components ─────────────────────────────────────────────────────

function MetricBox({ label, value }) {
  return (
    <div style={{ background: 'rgba(0,0,0,0.35)', borderRadius: 6, padding: '6px 12px', flex: '1 1 95px', minWidth: 85 }}>
      <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, marginBottom: 3, whiteSpace: 'nowrap' }}>{label}</div>
      <div style={{ color: '#fff', fontSize: 17, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </div>
  );
}

function MetricSection({ title, color, bgColor, rows }) {
  return (
    <div style={{
      border: `2px solid ${color}`, borderRadius: 8, padding: '10px 12px',
      background: bgColor, flex: 1, minWidth: 0,
    }}>
      <div style={{ color, fontWeight: 700, fontSize: 13, textAlign: 'center', marginBottom: 8 }}>{title}</div>
      {rows.map((row, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: i < rows.length - 1 ? 6 : 0 }}>
          {row.map((m) => <MetricBox key={m.label} label={m.label} value={m.value} />)}
        </div>
      ))}
    </div>
  );
}

// ── Hidden filter keys for this page ─────────────────────────────────────────

const HIDDEN_SUMMARY = new Set([
  'direction', 'contact_type', 'contact_id', 'agent_name',
  'disp_name', 'contact_end_reason', 'ani_called_back', 'hold_time',
  'agent_time', 'acw_time', 'active_talk_time', 'flagged', 'call_hour', 'day_of_week',
]);
const HIDDEN_CALLLOG = new Set(['flagged', 'day_of_week']);

const TAG_COUNT_COLS = [
  { title: 'Campaign', dataIndex: 'Campaign', key: 'Campaign', ellipsis: true, sorter: (a, b) => a.Campaign.localeCompare(b.Campaign) },
  { title: 'Tag',      dataIndex: 'Tag',      key: 'Tag',      ellipsis: true, sorter: (a, b) => a.Tag.localeCompare(b.Tag) },
  { title: 'Count',    dataIndex: 'TagCount', key: 'TagCount', width: 90, align: 'right', sorter: (a, b) => (a.TagCount ?? 0) - (b.TagCount ?? 0) },
];

// ── Summary tab column definitions ───────────────────────────────────────────

const CAMPAIGN_COLS = [
  { title: '#',           key: '_idx',           width: 35,  render: (_, __, i) => i + 1 },
  { title: 'Campaign',    dataIndex: 'Campaign',  ellipsis: true },
  { title: 'Direction',   dataIndex: 'Direction', width: 100 },
  { title: 'Media',       dataIndex: 'Media_Name',width: 100, ellipsis: true },
  { title: 'Record Count',dataIndex: 'RecordCount',  width: 100, align: 'right', render: NUM },
  { title: 'Handled Qty', dataIndex: 'HandledQty',   width: 90,  align: 'right', render: NUM },
  { title: 'Abandon Qty', dataIndex: 'AbandonQty',   width: 90,  align: 'right', render: NUM },
  { title: 'ASA',         dataIndex: 'ASA',           width: 85,  align: 'right', render: HMS },
  { title: 'AHT',         dataIndex: 'AHT',           width: 85,  align: 'right', render: HMS },
  { title: 'Avg Talk',    dataIndex: 'AvgTalk',       width: 85,  align: 'right', render: HMS },
  { title: 'Avg ACW',     dataIndex: 'AvgACW',        width: 85,  align: 'right', render: HMS },
  { title: 'Max Inqueue', dataIndex: 'MaxInqueue',    width: 100, align: 'right', render: HMS },
  { title: 'Max Abandon', dataIndex: 'MaxAbandonTime',width: 100, align: 'right', render: HMS },
  { title: 'Max Talk',    dataIndex: 'MaxTalk',       width: 85,  align: 'right', render: HMS },
  { title: 'Max ACW',    dataIndex: 'MaxACW',        width: 85,  align: 'right', render: HMS },
  { title: 'Called Support Qty', dataIndex: 'CalledSupportQty', width: 110, align: 'right', render: renderQtyPct },
  { title: 'Called Tier2 Qty',   dataIndex: 'CalledTier2Qty',   width: 110, align: 'right', render: renderQtyPct },
];

const SKILL_COLS = [
  { title: '#',                  key: '_idx',              width: 35,  render: (_, __, i) => i + 1 },
  { title: 'Campaign',           dataIndex: 'Campaign',    ellipsis: true },
  { title: 'Skill Name',         dataIndex: 'Skill_Name',  ellipsis: true },
  { title: 'Record Count',       dataIndex: 'RecordCount', width: 100, align: 'right', render: NUM },
  { title: 'Handled Qty',        dataIndex: 'HandledQty',  width: 90,  align: 'right', render: NUM },
  { title: 'Abandon Qty',        dataIndex: 'AbandonQty',  width: 90,  align: 'right', render: NUM },
  { title: 'ASA',                dataIndex: 'ASA',         width: 85,  align: 'right', render: HMS },
  { title: 'AHT',                dataIndex: 'AHT',         width: 85,  align: 'right', render: HMS },
  { title: 'Avg Talk',           dataIndex: 'AvgTalk',     width: 85,  align: 'right', render: HMS },
  { title: 'Avg ACW',            dataIndex: 'AvgACW',      width: 85,  align: 'right', render: HMS },
  { title: 'Max Inqueue',        dataIndex: 'MaxInqueue',     width: 100, align: 'right', render: HMS },
  { title: 'Max Abandon Time',   dataIndex: 'MaxAbandonTime', width: 110, align: 'right', render: HMS },
  { title: 'Max Talk',           dataIndex: 'MaxTalk',     width: 85,  align: 'right', render: HMS },
  { title: 'Max ACW',            dataIndex: 'MaxACW',      width: 85,  align: 'right', render: HMS },
  { title: 'Called Support Qty', dataIndex: 'CalledSupportQty', width: 110, align: 'right', render: renderQtyPct },
  { title: 'Called Tier2 Qty',   dataIndex: 'CalledTier2Qty',   width: 110, align: 'right', render: renderQtyPct },
];

const DISPO_COLS = [
  { title: '#',                  key: '_idx',          width: 35,  render: (_, __, i) => i + 1 },
  { title: 'Campaign',           dataIndex: 'Campaign',  ellipsis: true },
  { title: 'Disp Name',          dataIndex: 'Disp_Name', ellipsis: true },
  { title: 'Handled Qty',        dataIndex: 'HandledQty', width: 90, align: 'right', render: NUM },
  { title: 'AHT',                dataIndex: 'AHT',        width: 85, align: 'right', render: HMS },
  { title: 'Avg Talk',           dataIndex: 'AvgTalk',    width: 85, align: 'right', render: HMS },
  { title: 'Avg ACW',            dataIndex: 'AvgACW',     width: 85, align: 'right', render: HMS },
  { title: 'Max Talk',           dataIndex: 'MaxTalk',    width: 85, align: 'right', render: HMS },
  { title: 'Max ACW',            dataIndex: 'MaxACW',     width: 85, align: 'right', render: HMS },
  { title: 'Called Support Qty', dataIndex: 'CalledSupportQty', width: 110, align: 'right', render: NUM },
  { title: 'Called Tier2 Qty',   dataIndex: 'CalledTier2Qty',   width: 110, align: 'right', render: NUM },
];

// ── Data transform helpers ────────────────────────────────────────────────────

// allHours: the hours to show (so stacked charts line up hour for hour);
// missing: what an hour with no row shows (0 for counts, null for ASA).
function buildChartData(rows, valueField, suffix, allHours = null, missing = null) {
  if (!rows.length && !allHours?.length) return { data: [], campaigns: [] };
  const hours = allHours || [...new Set(rows.map((r) => r.Hour))].sort((a, b) => a - b);
  const campaigns = [...new Set(rows.map((r) => r.Campaign))].sort();
  const data = hours.map((h) => {
    const rec = { hour: h, label: fmtHour(h) };
    const hourRows = rows.filter((r) => r.Hour === h);
    campaigns.forEach((c) => {
      const match = hourRows.find((r) => r.Campaign === c);
      rec[`${c}_${suffix}`] = match != null ? (match[valueField] ?? null) : missing;
    });
    return rec;
  });
  return { data, campaigns };
}

function buildPivotGroups(rows, keyFn, metaFn) {
  if (!rows.length) return { pivotRows: [], activeHours: [], campaignSpans: {} };
  const activeHours = [...new Set(rows.map((r) => r.Hour))].sort((a, b) => a - b);
  const lookup = {};
  rows.forEach((r) => {
    const k = keyFn(r);
    if (!lookup[k]) lookup[k] = metaFn(r);
    lookup[k][r.Hour] = r.RecordCount;
  });
  const pivotRows = Object.values(lookup);
  pivotRows.sort((a, b) => {
    if (a._campaign !== b._campaign) return a._campaign.localeCompare(b._campaign);
    const tA = activeHours.reduce((s, h) => s + (a[h] || 0), 0);
    const tB = activeHours.reduce((s, h) => s + (b[h] || 0), 0);
    return tB - tA;
  });
  const campaignSpans = {};
  pivotRows.forEach((r, i) => {
    if (!campaignSpans[r._campaign]) campaignSpans[r._campaign] = { start: i, span: 0 };
    campaignSpans[r._campaign].span++;
  });
  return { pivotRows, activeHours, campaignSpans };
}

// ── Bar chart component ───────────────────────────────────────────────────────

function HourBarChart({ title, data, campaigns, metricKey, labelFmt, yFmt, hasAvg, avgLabel, alertHigher }) {
  if (!data.length) return null;
  const chartW = Math.max(700, data.length * Math.max(55, campaigns.length * 22 + 12));

  const totals = labelFmt ? [] : campaigns.map((c, i) => {
    const vals = data.map((row) => row[`${c}_${metricKey}`]).filter((v) => v != null);
    if (!vals.length) return { c, i, display: '—' };
    const sum = vals.reduce((a, b) => a + b, 0);
    return { c, i, display: sum.toLocaleString() };
  });

  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
        <Typography.Text strong>{title}</Typography.Text>
        {totals.length > 0 && (
          <span style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {totals.map(({ c, i, display }) => (
              <span key={c} style={{ fontSize: 11, padding: '1px 7px', borderRadius: 4, background: '#f5f5f5', border: `1px solid ${getColor(i)}55`, color: '#595959' }}>
                <span style={{ color: getColor(i), fontWeight: 600 }}>{c}</span>
                {': '}{display}
              </span>
            ))}
          </span>
        )}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <div style={{ width: chartW }}>
          <ResponsiveContainer width="100%" height={270}>
            <ComposedChart data={data} margin={{ top: 22, right: 10, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis
                tickFormatter={yFmt || undefined}
                width={yFmt ? 70 : 40}
                tick={{ fontSize: 11 }}
              />
              <Tooltip formatter={(v, name) => [labelFmt ? labelFmt(v) : (v != null ? Math.round(v * 10) / 10 : v), name]} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {campaigns.map((c, i) => {
                const baseColor = getColor(i);
                const key = `${c}_${metricKey}`;
                return (
                  <Bar key={c} dataKey={key} name={c} fill={baseColor} maxBarSize={36}>
                    {alertHigher && hasAvg && data.map((entry, idx) => {
                      const val = entry[key];
                      const avg = entry._dow_avg;
                      const overAvg = val != null && avg != null && val > avg;
                      return <Cell key={idx} fill={overAvg ? '#ff4d4f' : baseColor} />;
                    })}
                    <LabelList
                      dataKey={key}
                      position="top"
                      style={{ fontSize: 9, fill: baseColor }}
                      formatter={(v) => (v ? (labelFmt ? labelFmt(v) : v) : '')}
                    />
                  </Bar>
                );
              })}
              {hasAvg && (
                <Line
                  type="monotone"
                  dataKey="_dow_avg"
                  name={avgLabel || 'DOW Avg'}
                  stroke="#595959"
                  strokeDasharray="5 3"
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

// ── Pivot table component ─────────────────────────────────────────────────────

function PivotTable({ title, pivotRows, activeHours, campaignSpans, fixedCols, loading }) {
  const fixedCount = 1 + fixedCols.length;

  const campCol = useMemo(() => ({
    title: 'Campaign Name', dataIndex: '_campaign', fixed: 'left', width: 130, ellipsis: true,
    onCell: (record, rowIndex) => {
      const info = campaignSpans[record._campaign];
      if (!info) return {};
      return rowIndex === info.start ? { rowSpan: info.span } : { rowSpan: 0 };
    },
  }), [campaignSpans]);

  const hourCols = useMemo(() => activeHours.map((h) => ({
    title: fmtHour(h), key: `h${h}`, width: 60, align: 'right',
    render: (_, r) => { const v = r[h]; return (v != null && v > 0) ? v : '-'; },
  })), [activeHours]);

  const totalCol = useMemo(() => ({
    title: 'Grand total', key: '_total', width: 90, align: 'right',
    onHeaderCell: () => ({ style: { fontWeight: 700 } }),
    render: (_, r) => activeHours.reduce((s, h) => s + (r[h] || 0), 0) || '-',
  }), [activeHours]);

  const columns = [campCol, ...fixedCols, ...hourCols, totalCol];

  return (
    <div style={{ marginBottom: 28 }}>
      {title && <Typography.Text strong style={{ display: 'block', marginBottom: 6 }}>{title}</Typography.Text>}
      <Table
        rowKey={(_, i) => i}
        dataSource={pivotRows}
        columns={columns}
        size="small"
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={false}
        locale={{ emptyText: loading ? ' ' : 'No data' }}
        summary={(pageData) => {
          const totals = {};
          activeHours.forEach((h) => { totals[h] = pageData.reduce((s, r) => s + (r[h] || 0), 0); });
          const grand = Object.values(totals).reduce((s, v) => s + v, 0);
          return (
            <Table.Summary fixed>
              <Table.Summary.Row style={{ fontWeight: 700, background: '#fafafa' }}>
                <Table.Summary.Cell index={0} colSpan={fixedCount}>Grand total</Table.Summary.Cell>
                {activeHours.map((h, i) => (
                  <Table.Summary.Cell key={h} index={i + 1} align="right">
                    {totals[h] || '-'}
                  </Table.Summary.Cell>
                ))}
                <Table.Summary.Cell index={activeHours.length + 1} align="right">
                  {grand || '-'}
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          );
        }}
      />
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function CampaignStatsTodayPage() {
  const [activeTab,          setActiveTab]          = useState('by-hour');
  const [summaryData,        setSummaryData]        = useState(null);
  const [hourData,           setHourData]           = useState(null);
  const [tagData,            setTagData]            = useState(null);
  const [tagView,            setTagView]            = useState('Tag Count');
  const [callLogRows,        setCallLogRows]        = useState([]);
  const [callLogTotal,       setCallLogTotal]       = useState(0);
  const [callLogPage,        setCallLogPage]        = useState(1);
  const [callLogPageSize,    setCallLogPageSize]    = useState(100);
  const [callLogSort,        setCallLogSort]        = useState({ field: 'Time', order: 'descend' });
  const [selectedId,         setSelectedId]         = useState(null);
  const [panelOpen,          setPanelOpen]          = useState(false);
  const [loading,            setLoading]            = useState(false);
  const [hourLookbackWeeks,  setHourLookbackWeeks]  = useState(6);
  const [hourLookbackData,   setHourLookbackData]   = useState(null);
  const [hideHourAvg,        setHideHourAvg]        = useState(false);
  const callLogFiltersRef    = useRef({});
  const callLogSortRef       = useRef({ field: 'Time', order: 'descend' });
  const hourLookbackWeeksRef = useRef(6);
  hourLookbackWeeksRef.current = hourLookbackWeeks;

  const fetchCallLog = useCallback(async (filters, page, pageSize) => {
    const sort = callLogSortRef.current;
    try {
      const res = await api.get('/api/campaign-stats-today/call-log', {
        params: {
          ...filters,
          page,
          page_size:  pageSize,
          sort_by:    sort.field,
          sort_dir:   sort.order === 'ascend' ? 'asc' : 'desc',
        },
      });
      setCallLogRows(res.data.data ?? []);
      setCallLogTotal(res.data.total ?? 0);
      setCallLogPage(page);
    } catch {
      setCallLogRows([]);
      setCallLogTotal(0);
    }
  }, []);

  const fetchHourLookback = useCallback(async (campaign, date, weeks) => {
    if (!campaign || !date) { setHourLookbackData(null); return; }
    try {
      const res = await api.get('/api/campaign-stats-today/lookback-by-hour', {
        params: { campaign, date, weeks },
      });
      setHourLookbackData(res.data);
    } catch {
      setHourLookbackData(null);
    }
  }, []);

  // silent: the auto refresh -- no spinner, and a failed refresh keeps what
  // is on screen rather than blanking it.
  const fetchData = useCallback(async (filters = {}, { silent = false } = {}) => {
    if (!silent) setLoading(true);
    callLogFiltersRef.current = filters;
    try {
      const [sumRes, hourRes, tagRes] = await Promise.all([
        api.get('/api/campaign-stats-today/summary',   { params: filters }),
        api.get('/api/campaign-stats-today/by-hour',   { params: filters }),
        api.get('/api/campaign-stats-today/tag-stats', { params: filters }),
      ]);
      setSummaryData(sumRes.data);
      setHourData(hourRes.data);
      setTagData(tagRes.data);
      await fetchCallLog(filters, 1, callLogPageSize);
      fetchHourLookback(filters.campaign, filters.start_date, hourLookbackWeeksRef.current);
      setLastLoaded(dayjs());
    } catch {
      if (silent) return;
      setSummaryData(null);
      setHourData(null);
      setTagData(null);
      setCallLogRows([]);
      setCallLogTotal(0);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [fetchCallLog, callLogPageSize, fetchHourLookback]);

  // Auto refresh: the last search again every 10 minutes. Waits while the
  // browser tab is hidden, and catches up when it is shown again.
  const [lastLoaded, setLastLoaded] = useState(null);
  const lastLoadedRef = useRef(null);
  lastLoadedRef.current = lastLoaded;
  useEffect(() => {
    const due = () => lastLoadedRef.current && callLogFiltersRef.current?.campaign
      && dayjs().diff(lastLoadedRef.current, 'minute', true) >= REFRESH_MINUTES;
    const tick = () => {
      if (document.visibilityState === 'visible' && due()) fetchData(callLogFiltersRef.current, { silent: true });
    };
    const timer = setInterval(tick, 30 * 1000);
    document.addEventListener('visibilitychange', tick);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', tick); };
  }, [fetchData]);

  const handleCallLogPageChange = useCallback((page, pageSize) => {
    setCallLogPageSize(pageSize);
    fetchCallLog(callLogFiltersRef.current, page, pageSize);
  }, [fetchCallLog]);

  const handleCallLogSortChange = useCallback((field, order) => {
    callLogSortRef.current = { field, order };
    setCallLogSort({ field, order });
    fetchCallLog(callLogFiltersRef.current, 1, callLogPageSize);
  }, [fetchCallLog, callLogPageSize]);

  useEffect(() => {
    const f = callLogFiltersRef.current;
    if (!f?.campaign) return;
    fetchHourLookback(f.campaign, f.start_date, hourLookbackWeeks);
  }, [hourLookbackWeeks, fetchHourLookback]);

  const ib = summaryData?.inbound  || {};
  const ch = summaryData?.chat     || {};
  const ob = summaryData?.outbound || {};

  // One set of hours for the three By Hour charts, so 9AM sits under 9AM:
  // every hour any of them has.
  const chartHours = useMemo(() => [...new Set([
    ...(hourData?.offered_by_hour ?? []), ...(hourData?.abandon_by_hour ?? []), ...(hourData?.asa_by_hour ?? []),
  ].map((r) => r.Hour))].sort((a, b) => a - b), [hourData]);

  const { data: offeredData,  campaigns: offeredCampaigns  } = useMemo(
    () => buildChartData(hourData?.offered_by_hour ?? [], 'RecordCount', 'offered', chartHours, 0), [hourData, chartHours]);

  const { data: abandonData,  campaigns: abandonCampaigns  } = useMemo(
    () => buildChartData(hourData?.abandon_by_hour ?? [], 'RecordCount', 'abandon', chartHours, 0), [hourData, chartHours]);

  const { data: asaData,      campaigns: asaCampaigns      } = useMemo(
    () => buildChartData(hourData?.asa_by_hour ?? [],     'ASA',         'asa',     chartHours, null), [hourData, chartHours]);

  const avgHourMap = useMemo(() => {
    if (!hourLookbackData?.length) return null;
    const m = {};
    hourLookbackData.forEach((r) => { m[r.Hour] = r; });
    return m;
  }, [hourLookbackData]);

  // For the Calls & Staff tab: offered calls per 15 minutes (all campaigns
  // picked), keyed by the slot's index in the day (hour * 4 + quarter).
  const offeredByQuarter = useMemo(() => {
    const m = {};
    (hourData?.offered_by_quarter || []).forEach((r) => { m[r.Hour * 4 + r.Quarter] = r.RecordCount || 0; });
    return m;
  }, [hourData]);
  // Handled: the offered calls not abandoned.
  const handledByQuarter = useMemo(() => {
    const m = {};
    (hourData?.offered_by_quarter || []).forEach((r) => { m[r.Hour * 4 + r.Quarter] = r.Handled || 0; });
    return m;
  }, [hourData]);

  const offeredChartData = useMemo(() => {
    if (!avgHourMap || hideHourAvg) return { data: offeredData, hasAvg: false };
    return {
      data: offeredData.map((d) => ({ ...d, _dow_avg: avgHourMap[d.hour]?.AvgOffered ?? null })),
      hasAvg: true,
    };
  }, [offeredData, avgHourMap, hideHourAvg]);

  const abandonChartData = useMemo(() => {
    if (!avgHourMap || hideHourAvg) return { data: abandonData, hasAvg: false };
    return {
      data: abandonData.map((d) => ({ ...d, _dow_avg: avgHourMap[d.hour]?.AvgAbandon ?? null })),
      hasAvg: true,
    };
  }, [abandonData, avgHourMap, hideHourAvg]);

  const asaChartData = useMemo(() => {
    if (!avgHourMap || hideHourAvg) return { data: asaData, hasAvg: false };
    return {
      data: asaData.map((d) => ({ ...d, _dow_avg: avgHourMap[d.hour]?.AvgASA ?? null })),
      hasAvg: true,
    };
  }, [asaData, avgHourMap, hideHourAvg]);

  const { pivotRows: campRows, activeHours: campHours, campaignSpans: campSpans } = useMemo(
    () => buildPivotGroups(
      hourData?.campaign_hour_rows ?? [],
      (r) => `${r.Campaign}||${r.Direction}||${r.Media_Name}`,
      (r) => ({ _campaign: r.Campaign, _direction: r.Direction, _media: r.Media_Name }),
    ), [hourData]);

  const { pivotRows: dispoRows, activeHours: dispoHours, campaignSpans: dispoSpans } = useMemo(
    () => buildPivotGroups(
      hourData?.dispo_hour_rows ?? [],
      (r) => `${r.Campaign}||${r.Disp_Name}`,
      (r) => ({ _campaign: r.Campaign, _dispo: r.Disp_Name }),
    ), [hourData]);

  const campFixedCols = useMemo(() => [
    { title: 'Direction', dataIndex: '_direction', fixed: 'left', width: 100 },
    { title: 'Media',     dataIndex: '_media',     fixed: 'left', width: 100, ellipsis: true },
  ], []);

  const dispoFixedCols = useMemo(() => [
    { title: 'Disp Name', dataIndex: '_dispo', fixed: 'left', width: 160, ellipsis: true },
  ], []);

  const tagHourPivot = useMemo(() => {
    const rows = tagData?.tag_hour_rows ?? [];
    if (!rows.length) return { cols: [], data: [] };
    const hours  = [...new Set(rows.map((r) => r.Hour))].sort((a, b) => a - b);
    const combos = [...new Set(rows.map((r) => `${r.Campaign}||${r.Tag}`))];
    const lookup = {};
    rows.forEach((r) => { lookup[`${r.Campaign}||${r.Tag}||${r.Hour}`] = r.TagCount; });
    const cols = [
      { title: 'Campaign', dataIndex: '_campaign', key: '_campaign', width: 130, fixed: 'left', ellipsis: true },
      { title: 'Tag',      dataIndex: '_tag',      key: '_tag',      width: 150, fixed: 'left', ellipsis: true },
      ...hours.map((h) => ({
        title: fmtHour(h), dataIndex: String(h), key: String(h), width: 65, align: 'right',
        render: (v) => (v ?? '-'),
      })),
    ];
    const data = combos.map((ck) => {
      const [campaign, tag] = ck.split('||');
      const row = { _key: ck, _campaign: campaign, _tag: tag };
      hours.forEach((h) => { row[String(h)] = lookup[`${ck}||${h}`] ?? null; });
      return row;
    });
    return { cols, data };
  }, [tagData]);

  // Abandoned as a share of offered.
  const abandonPct = ib.Offered ? `${((ib.AbandonQty || 0) / ib.Offered * 100).toFixed(1)}%` : '—';
  const inboundRows = [
    [
      { label: 'Offered',          value: NUM(ib.Offered) },
      { label: 'Handled Qty',      value: NUM(ib.HandledQty) },
      { label: 'Abandon Qty',      value: NUM(ib.AbandonQty) },
      { label: 'Abandon %',        value: abandonPct },
    ],
    [
      { label: 'ASA',              value: HMS(ib.ASA) },
      { label: 'Avg Talk',         value: HMS(ib.AvgTalk) },
      { label: 'Avg ACW',          value: HMS(ib.AvgACW) },
      { label: 'AHT',              value: HMS(ib.AHT) },
    ],
  ];

  const chatRows = [
    [{ label: 'Offered',     value: NUM(ch.Offered) }, { label: 'Handled Qty', value: NUM(ch.HandledQty) }],
    [{ label: 'ASA',         value: HMS(ch.ASA) },     { label: 'AHT',         value: HMS(ch.AHT) }],
  ];

  const outboundRows = [
    [
      { label: 'Leg 1 Calls Attempted', value: NUM(ob.Offered) },
      { label: 'Leg 1 Calls Handled',   value: NUM(ob.HandledOB) },
    ],
    [
      { label: 'AHT',      value: HMS(ob.AHT) },
      { label: 'Avg Talk', value: HMS(ob.AvgTalk) },
      { label: 'Avg ACW',  value: HMS(ob.AvgACW) },
    ],
  ];

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Campaign Stats Today</Typography.Title>
        {lastLoaded && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Updated {lastLoaded.format('h:mm A')} · refreshes every {REFRESH_MINUTES} minutes
          </Typography.Text>
        )}
      </div>

      <FilterPanel
        sessionKey="campaign-stats-today"
        onSearch={fetchData}
        loading={loading}
        hideAIFilters
        hiddenFilters={activeTab === 'call-log' ? HIDDEN_CALLLOG : HIDDEN_SUMMARY}
        defaultFilters={{ direction: [], contact_type: [] }}
        defaultDateRange={dayjs()}
        singleDate
        noPastDate
        requireCampaign
        showCalledFilters={activeTab === 'call-log'}
        searchNote={<>To see historical call metrics, go to page <Link to="/campaign-stats">Campaign Stats</Link></>}
      />

      <Spin spinning={loading}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'by-hour',
              label: 'By Hour',
              children: (
                <>
                  {/* ── Top metric boxes ── */}
                  <div style={{
                    display: 'flex', gap: 12, marginBottom: 20,
                    background: '#111', borderRadius: 10, padding: 12,
                  }}>
                    <MetricSection
                      title="Inbound Phone"
                      color="#fbbf24" bgColor="rgba(251,191,36,0.06)"
                      rows={inboundRows}
                    />
                    <MetricSection
                      title="Chat"
                      color="#22d3ee" bgColor="rgba(34,211,238,0.06)"
                      rows={chatRows}
                    />
                    <MetricSection
                      title="Outbound"
                      color="#f59e0b" bgColor="rgba(245,158,11,0.06)"
                      rows={outboundRows}
                    />
                  </div>

                  <Space style={{ marginBottom: 16 }} wrap>
                    <AntTooltip title="For this same Day of Week how many weeks to look back to get the avg">
                      <span style={{ fontSize: 13 }}>DOW Look back (wks):</span>
                    </AntTooltip>
                    <Select
                      size="small"
                      style={{ width: 70 }}
                      value={hourLookbackWeeks}
                      onChange={setHourLookbackWeeks}
                      options={LOOKBACK_OPTIONS}
                    />
                    <Checkbox checked={hideHourAvg} onChange={(e) => setHideHourAvg(e.target.checked)}>
                      Hide averages
                    </Checkbox>
                  </Space>
                  <HourBarChart
                    title="Offered Inbound Campaign By Hour"
                    data={offeredChartData.data}
                    campaigns={offeredCampaigns}
                    metricKey="offered"
                    hasAvg={offeredChartData.hasAvg}
                    avgLabel={`DOW Avg (${hourLookbackWeeks} wks)`}
                    alertHigher
                  />
                  <HourBarChart
                    title="Abandon Inbound Campaign By Hour"
                    data={abandonChartData.data}
                    campaigns={abandonCampaigns}
                    metricKey="abandon"
                    hasAvg={abandonChartData.hasAvg}
                    avgLabel={`DOW Avg (${hourLookbackWeeks} wks)`}
                  />
                  <HourBarChart
                    title="ASA Inbound Campaign By Hour"
                    data={asaChartData.data}
                    campaigns={asaCampaigns}
                    metricKey="asa"
                    labelFmt={fmtHMS}
                    yFmt={fmtHMS}
                    hasAvg={asaChartData.hasAvg}
                    avgLabel={`DOW Avg (${hourLookbackWeeks} wks)`}
                  />

                  <PivotTable
                    title="Campaign By Hour"
                    pivotRows={campRows}
                    activeHours={campHours}
                    campaignSpans={campSpans}
                    fixedCols={campFixedCols}
                    loading={loading}
                  />

                  <PivotTable
                    title="Inbound Dispos By Hour"
                    pivotRows={dispoRows}
                    activeHours={dispoHours}
                    campaignSpans={dispoSpans}
                    fixedCols={dispoFixedCols}
                    loading={loading}
                  />
                </>
              ),
            },
            {
              // Offered calls beside the staffing of the shift departments.
              key: 'staffing',
              label: 'Calls & Staff',
              children: (
                <StaffingTab
                  date={callLogFiltersRef.current?.start_date}
                  campaign={callLogFiltersRef.current?.campaign}
                  offeredByQuarter={offeredByQuarter}
                  handledByQuarter={handledByQuarter}
                  refreshKey={lastLoaded ? lastLoaded.valueOf() : 0}
                />
              ),
            },
            {
              key: 'summary',
              label: 'Summary',
              children: (
                <>
                  <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>Campaign</Typography.Text>
                  <Table
                    rowKey={(r) => `${r.Campaign}-${r.Direction}-${r.Media_Name}`}
                    dataSource={summaryData?.campaign_rows ?? []}
                    columns={CAMPAIGN_COLS}
                    size="small"
                    scroll={{ x: 'max-content' }}
                    pagination={false}
                    locale={{ emptyText: loading ? ' ' : 'No data' }}
                    style={{ marginBottom: 24 }}
                  />
                  <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>Top Skills</Typography.Text>
                  <Table
                    rowKey={(r) => `${r.Campaign}-${r.Skill_Name}`}
                    dataSource={summaryData?.skill_rows ?? []}
                    columns={SKILL_COLS}
                    size="small"
                    scroll={{ x: 'max-content' }}
                    pagination={false}
                    locale={{ emptyText: loading ? ' ' : 'No data' }}
                    style={{ marginBottom: 24 }}
                  />
                  <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
                    Top Inbound Dispos
                  </Typography.Text>
                  <Table
                    rowKey={(r) => `${r.Campaign}-${r.Disp_Name}`}
                    dataSource={summaryData?.dispo_rows ?? []}
                    columns={DISPO_COLS}
                    size="small"
                    scroll={{ x: 'max-content' }}
                    pagination={{ defaultPageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} rows` }}
                    locale={{ emptyText: loading ? ' ' : 'No data' }}
                  />
                </>
              ),
            },
            {
              key: 'tags',
              label: 'Tags',
              children: (
                <>
                  <div style={{ marginBottom: 12 }}>
                    <Segmented
                      options={['Tag Count', 'By Hour']}
                      value={tagView}
                      onChange={setTagView}
                      size="small"
                    />
                  </div>

                  {tagView === 'Tag Count' && (
                    <Table
                      rowKey={(r) => `${r.Campaign}||${r.Tag}`}
                      dataSource={tagData?.tag_rows ?? []}
                      columns={TAG_COUNT_COLS}
                      loading={loading}
                      size="small"
                      pagination={{ defaultPageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} rows` }}
                      locale={{ emptyText: loading ? ' ' : tagData === null ? 'Select a campaign and click Search.' : 'No results found.' }}
                    />
                  )}

                  {tagView === 'By Hour' && (
                    <Table
                      rowKey="_key"
                      dataSource={tagHourPivot.data}
                      columns={tagHourPivot.cols}
                      loading={loading}
                      size="small"
                      pagination={false}
                      scroll={{ x: 'max-content' }}
                      locale={{ emptyText: loading ? ' ' : tagData === null ? 'Select a campaign and click Search.' : 'No results found.' }}
                    />
                  )}
                </>
              ),
            },
            {
              key: 'call-log',
              label: 'Call Log Today',
              children: (
                <ResultsTable
                  data={callLogRows}
                  loading={loading}
                  total={callLogTotal}
                  page={callLogPage}
                  pageSize={callLogPageSize}
                  onPageChange={handleCallLogPageChange}
                  sortState={callLogSort}
                  onSortChange={handleCallLogSortChange}
                  onRowClick={(row) => { setSelectedId(row.ContactID); setPanelOpen(true); }}
                />
              ),
            },
          ]}
        />
      </Spin>

      <SidePanel
        contactId={selectedId}
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
      />
    </>
  );
}
